import {
  AggregatedSubtotal,
  AggregatedTaxAwarePrice,
  ClusivitySeparatedPrices,
  Price,
  SubtotalRequests,
  aggregateTaxAwarePrices,
  getClusivitySeparatedPrices,
  identifyFee,
  mapVistaCartPrices,
} from '@vp/om-tax-display';
import {
  DETAILED_TAX_DISPLAY,
  ORDER_LEVEL,
  PRODUCT_IDENTIFIER,
  SHIPPING_IDENTIFIER,
} from '../constants/taxDisplayOptions';
import { OrderDetails } from '../types/orderDetails';

export type KeyedClusivitySeparatedPrices = Record<
  string,
  ClusivitySeparatedPrices<{}>[]
>;

/**
 * Overview:
 * Given a BOP order this function will return its item, item group, and shipping prices with taxes included AND excluded (aka "clusivity separated" prices)
 * leverages the {@link https://gitlab.com/vistaprint-org/order-management/libraries2/om-tax-display} to format pricing data, so it can be consumed by the @vp/component-library pricing components
 *
 * How it works
 * 1. Reformats the pricing data on a BOP order to group pricing components with their respective taxes (bucketed into "FEE" and "ATTRIBUTE" types)
 *    This transforms the BOP pricing data into a format consumable by the @vp/om-tax-display helper functions.
 * 2. Uses the @vp/om-tax-display helper functions to generate maps of tax [in/ex]clusive prices for items and item group prices,
 *    as well as a list of tax [in/ex]clusive prices for shipping prices
 * 3. Returns those maps and list so they can be used downstream to generate the required props of @vp/component-library pricing components
 *
 * Input: BOP order (from OrderDetails UI route)
 *
 * Output: Item, item group, and shipping prices separated by tax inclusive and tax exclusive
 *  itemIdToTaxInclusiveAndExclusivePricesMap: {itemId: [prices separated into tax inclusive and tax exclusive buckets]} - a map of item ids to their tax inclusive & exclusive component prices
 *  itemGroupIdToTaxInclusiveAndExclusivePricesMap: {itemGroupId: [prices separated into tax inclusive and tax exclusive buckets]} - a map of item group ids to their tax inclusive & exclusive component prices
 *  itemIdToTaxInclusiveAndExclusiveShippingPricesMap: {lineItemId: [prices separated into tax inclusive and tax exclusive buckets]} a map of tax inclusive & exclusive order-level shipping prices
 */
export function getTaxInclusiveAndExclusivePrices(order: OrderDetails) {
  const itemIdToTaxInclusiveAndExclusivePricesMap: KeyedClusivitySeparatedPrices =
    {};
  const itemGroupIdToTaxInclusiveAndExclusivePricesMap: KeyedClusivitySeparatedPrices =
    {};
  const itemIdToTaxInclusiveAndExclusiveShippingPricesMap: KeyedClusivitySeparatedPrices =
    {};

  const lineItems = order?.lineItems;

  /*
   * LINE ITEM PRICING
   * For each line item, group its pricing components and taxes by type (`FEE` or `ATTRIBUTE`)
   * Then map each line item to a list of their tax [in/ex]clusive component prices
   * */

  lineItems?.forEach(li => {
    // product pricing
    const { pricingData, id, shippingPricingData } = li;
    if (pricingData) {
      const { components = [], taxes = [] } = pricingData;
      // Re-format the item's pricing data from the BOP response to group pricing components with their respective taxes
      // ex) product attributes and fees may be taxed differently and need to be separated at this step
      // Note: mapVistaCartPrices is a mapper shared between cart and the BOP item contract. We plan to rename this method to be more generic
      const itemIdToPricesMap = mapVistaCartPrices(
        components.map(component => ({
          ...component,
          identifiers: [PRODUCT_IDENTIFIER, component.name], // add a 'product' tag, so we can later aggregate based on this identifier
        })),
        taxes
      );
      // Bucket the item's prices into tax inclusive and exclusive prices
      const taxInclusiveAndExclusiveItemPrices: ClusivitySeparatedPrices<{}>[] =
        itemIdToPricesMap.map(price =>
          getClusivitySeparatedPrices(price.components, price.taxes)
        );
      // Map the item ID to its tax [in/ex]clusive prices
      itemIdToTaxInclusiveAndExclusivePricesMap[id] =
        taxInclusiveAndExclusiveItemPrices;

      /*
       * ITEM GROUP PRICING
       * If the item is in a group, map that groupId to its tax [in/ex]clusive prices
       * If that groupId already exists in the map, add this item's tax [in/ex]clusive prices
       */

      const groupId = li.groupInfo?.groupId;
      if (groupId) {
        // Check if tax [in/ex]clusive prices already exist for this group
        const currentGroupPrices =
          itemGroupIdToTaxInclusiveAndExclusivePricesMap[groupId] || [];

        // Push the prices for this item to the group's mapped prices
        currentGroupPrices.push(...taxInclusiveAndExclusiveItemPrices);

        // Update the map to point to this updated list
        itemGroupIdToTaxInclusiveAndExclusivePricesMap[groupId] =
          currentGroupPrices.flat();
      }
      if (shippingPricingData) {
        const {
          components: shippingComponents = [],
          taxes: shippingTaxes = [],
        } = shippingPricingData;

        const mappedShippingPrices = mapVistaCartPrices(
          shippingComponents.map(component => ({
            ...component,
            // tag the shipping components with their fee identifier OR 'shipping' if not a fee
            identifiers: [identifyFee(component) ?? SHIPPING_IDENTIFIER],
          })),
          shippingTaxes
        );

        itemIdToTaxInclusiveAndExclusiveShippingPricesMap[id] =
          mappedShippingPrices.map(price =>
            getClusivitySeparatedPrices(price.components, price.taxes)
          );
      }
    }
  });

  return {
    itemIdToTaxInclusiveAndExclusivePricesMap,
    itemGroupIdToTaxInclusiveAndExclusivePricesMap,
    itemIdToTaxInclusiveAndExclusiveShippingPricesMap,
  };
}

/**
 * Gets a list of item ids that are associated with the provided fee key
 * @param itemIdToTaxInclusiveAndExclusiveShippingPricesMap
 * @param feeKey fee key to check against
 * @returns list of item ids associated with given fee key
 */
const getItemIdsForFeeKey = (
  keyedClusivitySeparatedPrices: KeyedClusivitySeparatedPrices,
  feeKey: string
) => {
  const itemIds: string[] = [];
  Object.entries(keyedClusivitySeparatedPrices).forEach(([itemId, prices]) => {
    const identifiers: string[] = [];
    prices.forEach((price: ClusivitySeparatedPrices<{}>) => {
      price.inclusivePrices.forEach((ip: Price) => {
        ip.identifiers?.forEach(id => identifiers.push(id));
      });
    });
    const pricesArrayHasFeeKey = identifiers.includes(feeKey);
    if (pricesArrayHasFeeKey) {
      itemIds.push(itemId);
    }
  });
  return itemIds;
};

export interface FeeSubtotal extends AggregatedSubtotal {
  rowLabelKey: string;
  applicableItems: string[];
}

const getFeeSubtotalsFromAggregatedTaxAwarePrice = (
  itemIdToTaxInclusiveAndExclusiveShippingPricesMap: KeyedClusivitySeparatedPrices,
  aggregatedPrice: AggregatedTaxAwarePrice
): FeeSubtotal[] => {
  const feeSubtotals: FeeSubtotal[] = [];
  const { fees } = aggregatedPrice;
  for (const [feeName, value] of fees.entries()) {
    const feeSubtotal = value as FeeSubtotal;
    feeSubtotal.rowLabelKey = feeName;
    feeSubtotal.applicableItems = getItemIdsForFeeKey(
      itemIdToTaxInclusiveAndExclusiveShippingPricesMap,
      feeName
    );
    feeSubtotals.push(feeSubtotal);
  }
  return feeSubtotals;
};

/**
 * Overview:
 * Given a list of item and shipping prices with taxes included AND excluded (aka "clusivity separated" prices) returns order total, and product / shipping subtotal pricing objects which respect tax rules
 * Leverages the {@link https://gitlab.com/vistaprint-org/order-management/libraries2/om-tax-display} to generate prices with tax rules (inclusivity and formatting) applied.
 *
 * How it works
 * 1. Generate a list of order tax-inclusive and exclusive prices as an aggregation of item-level and shipping-level tax-inclusive and exclusive prices
 *      Note: this means we are calculating the order total in the UI as a sum of shipping and product prices
 * 2. Generate subtotals requests for `product total` and `shipping total` using the identifiers added in {@see getTaxInclusiveAndExclusivePrices}
 * 3. Uses the @vp/om-tax-display to generate a tax aware order price with subtotals for product and shipping
 * 3. Returns tax aware order total, product subtotal, and shipping subtotal pricing objects
 *
 * Input:
 *  itemIdToTaxInclusiveAndExclusivePricesMap: {itemId: [prices separated into tax inclusive and tax exclusive buckets]} - a map of item ids to their tax inclusive & exclusive component prices
 *  itemIdToTaxInclusiveAndExclusiveShippingPricesMap: {itemId: [prices separated into tax inclusive and tax exclusive buckets]} - a map of item ids to a list of tax inclusive & exclusive order-level shipping prices
 */
export function getTaxAwareOrderSummaryPricing({
  itemIdToTaxInclusiveAndExclusivePricesMap,
  itemIdToTaxInclusiveAndExclusiveShippingPricesMap,
}: {
  itemIdToTaxInclusiveAndExclusivePricesMap: KeyedClusivitySeparatedPrices;
  itemIdToTaxInclusiveAndExclusiveShippingPricesMap: KeyedClusivitySeparatedPrices;
}) {
  // create a list of all tax-inclusive and tax-exclusive product prices
  const taxInclusiveAndExclusiveOrderPrices = Object.values(
    itemIdToTaxInclusiveAndExclusivePricesMap
  ).flat();
  // create a subtotal request for any prices tagged with 'product' (and aggregate on the order level)
  const subtotalRequests: SubtotalRequests[] = [
    {
      key: PRODUCT_IDENTIFIER,
      level: ORDER_LEVEL,
    },
  ];

  if (itemIdToTaxInclusiveAndExclusiveShippingPricesMap) {
    // Add shipping and product prices to generate a list of all tax-inclusive and tax-exclusive prices for the order
    taxInclusiveAndExclusiveOrderPrices.push(
      ...Object.values(itemIdToTaxInclusiveAndExclusiveShippingPricesMap).flat()
    );
    // request subtotals for any prices tagged with 'shipping' (and aggregate on the order level)
    subtotalRequests.push({
      key: SHIPPING_IDENTIFIER,
      level: ORDER_LEVEL,
    });
  }

  // get formatted taxAware orderTotalPrice and subtotals for shippingTotal and productTotal
  const taxAwareOrderTotalPrice = aggregateTaxAwarePrices(
    taxInclusiveAndExclusiveOrderPrices,
    ORDER_LEVEL,
    DETAILED_TAX_DISPLAY,
    subtotalRequests,
    {} // OrderDetails doesn't respect the VAT display toggle, so we pass an empty object for taxDisplayContext
  );
  // get tax aware price for `product` subtotal
  const taxAwareProductTotalPrice =
    taxAwareOrderTotalPrice.subtotals.get(PRODUCT_IDENTIFIER);
  // get tax aware price for `shipping` subtotal
  const taxAwareShippingTotalPrice =
    taxAwareOrderTotalPrice.subtotals.get(SHIPPING_IDENTIFIER);

  // currently the only supported fees returned from tax display lib are shipping fees
  const shippingFees = getFeeSubtotalsFromAggregatedTaxAwarePrice(
    itemIdToTaxInclusiveAndExclusiveShippingPricesMap,
    taxAwareOrderTotalPrice
  );

  return {
    taxAwareOrderTotalPrice,
    taxAwareProductTotalPrice,
    taxAwareShippingTotalPrice,
    shippingFees,
  };
}

export const _test = {
  getFeeSubtotalsFromAggregatedTaxAwarePrice,
  getItemIdsForFeeKey,
};
